import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSetRecoilState } from 'recoil';

import {
  ClipboardCopyIcon,
  ExclamationCircleIcon,
  LocationMarkerIcon,
  TruckIcon,
  UserIcon,
} from '@heroicons/react/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/solid';
import {
  submitGiftDeliveryAddress,
  updateGiftDeliveryAddress,
  viewGiftDelivery,
} from '@shared/api/giftDelivery/giftDelivery.controller';
import {
  GiftDeliveryAddressDto,
  GiftDeliveryPublicResponse,
  GiftDeliveryStatus,
} from '@shared/api/giftDelivery/giftDelivery.interface';
import { Loading } from '@shared/components/Loading';
import Modal from '@shared/components/Modal';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { BucketImg } from '@shared/components/BucketImg';
import type { Address } from 'react-daum-postcode';
import DaumPostcode from 'react-daum-postcode';
import { useParams } from 'react-router-dom';

import { getGiftDeliveryByCode } from '../../../@shared/api/giftDelivery/giftDelivery.controller';
import { ContactEmailLink } from '../components/ContactEmailLink';

const ADDRESS_MODAL_ID = 'delivery-address-submit-search';

const formatPhoneNumber = (value: string) => {
  const numbers = value.replace(/[^0-9]/g, '');
  if (numbers.length <= 3) return numbers;
  if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
};

const getDeliveryStatusMessage = (status: GiftDeliveryStatus) => {
  switch (status) {
    case GiftDeliveryStatus.WaitingAddress:
      return null;
    case GiftDeliveryStatus.AddressSubmitted:
      return {
        type: 'info' as const,
        title: '배송지 입력이 완료되었습니다',
        message: '입력하신 주소로 상품이 배송될 예정입니다.',
      };
    case GiftDeliveryStatus.Preparing:
      return {
        type: 'info' as const,
        title: '상품 준비중입니다',
        message: '입력하신 주소로 곧 배송이 시작됩니다.',
      };
    case GiftDeliveryStatus.Shipping:
      return {
        type: 'info' as const,
        title: '배송중입니다',
        message: '택배사와 운송장번호를 확인해 주세요.',
      };
    case GiftDeliveryStatus.Delivered:
      return {
        type: 'success' as const,
        title: '배송이 완료되었습니다',
        message: '상품이 정상적으로 배송되었습니다. 감사합니다.',
      };
    case GiftDeliveryStatus.Canceled:
      return {
        type: 'error' as const,
        title: '취소되었습니다',
        message: '이 배송은 취소되었습니다.',
      };
    default:
      return null;
  }
};

const DELIVERY_STEPS: { key: GiftDeliveryStatus; label: string }[] = [
  { key: GiftDeliveryStatus.AddressSubmitted, label: '배송지 입력' },
  { key: GiftDeliveryStatus.Preparing, label: '상품 준비' },
  { key: GiftDeliveryStatus.Shipping, label: '배송중' },
  { key: GiftDeliveryStatus.Delivered, label: '배송완료' },
];

const getDeliveryStepIndex = (status: GiftDeliveryStatus): number => {
  switch (status) {
    case GiftDeliveryStatus.WaitingAddress:
    case GiftDeliveryStatus.AddressSubmitted:
      return 0;
    case GiftDeliveryStatus.Preparing:
      return 1;
    case GiftDeliveryStatus.Shipping:
      return 2;
    case GiftDeliveryStatus.Delivered:
      return 3;
    default:
      return -1;
  }
};

const DeliveryTimeline: FC<{ status: GiftDeliveryStatus }> = ({ status }) => {
  const isCanceled = status === GiftDeliveryStatus.Canceled;
  const currentStep = getDeliveryStepIndex(status);

  if (isCanceled) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-xl bg-red-50 py-3">
        <ExclamationCircleIcon className="h-4 w-4 text-red-400" />
        <span className="text-sm font-bold text-red-500">취소된 배송입니다</span>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between px-1">
      {DELIVERY_STEPS.map((step, idx) => {
        const isActive = idx <= currentStep;
        const isCurrent = idx === currentStep;
        return (
          <div key={step.key} className="flex flex-1 flex-col items-center">
            <div className="relative flex w-full items-center justify-center">
              {idx > 0 && (
                <div
                  className={`absolute right-1/2 h-0.5 w-full ${
                    idx <= currentStep ? 'bg-stone-500' : 'bg-stone-200'
                  }`}
                />
              )}
              <div
                className={`relative z-10 flex h-6 w-6 items-center justify-center rounded-full transition-all ${
                  isCurrent
                    ? 'bg-stone-700 shadow-md shadow-stone-300'
                    : isActive
                    ? 'bg-stone-500'
                    : 'bg-stone-200'
                }`}
              >
                {isActive ? (
                  <CheckCircleSolidIcon className="h-3.5 w-3.5 text-white" />
                ) : (
                  <div className="h-1.5 w-1.5 rounded-full bg-stone-400" />
                )}
              </div>
            </div>
            <span
              className={`mt-1.5 text-[11px] font-medium ${
                isCurrent
                  ? 'text-stone-800'
                  : isActive
                  ? 'text-stone-500'
                  : 'text-stone-300'
              }`}
            >
              {step.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const GiftDeliveryAddressSubmitPage: FC = function GiftDeliveryAddressSubmitPage() {
  const { deliveryCode } = useParams<{ deliveryCode: string }>();

  const [delivery, setDelivery] = useState<GiftDeliveryPublicResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // 폼 상태
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [address, setAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [addressDetail, setAddressDetail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editPhoneNumber, setEditPhoneNumber] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editPostalCode, setEditPostalCode] = useState('');
  const [editAddressDetail, setEditAddressDetail] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);

  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const handleCopyTracking = useCallback((trackingNumber: string) => {
    navigator.clipboard.writeText(trackingNumber).then(() => {
      setCopied(true);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
    });
  }, []);

  // 주소 검색 모달
  const setAddressModalOpen = useSetRecoilState(simpleOpenAtomFamily(ADDRESS_MODAL_ID));

  // 배송 조회
  useEffect(() => {
    if (!deliveryCode) return;
    setLoading(true);
    getGiftDeliveryByCode(deliveryCode)
      .then((data) => {
        setDelivery(data);
        // view 처리
        viewGiftDelivery(data._id).catch(console.error);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [deliveryCode]);

  // 상품 정보
  const imageUrl = useMemo(() => {
    if (!delivery?.giftItem) return '';
    return (
      delivery.giftItem.giftItemInfo?.image?.url || delivery.giftItem.image?.url || ''
    );
  }, [delivery]);

  const itemName =
    delivery?.giftItem?.name || delivery?.giftItem?.giftItemInfo?.name || '';
  const detailInfo = delivery?.giftItem?.giftItemInfo?.detailInfo || '';
  const courtesyContent = delivery?.giftOrder?.courtesyContent;

  // 주소 검색 완료
  const handleAddressComplete = useCallback(
    (data: Address) => {
      const fullAddress =
        data.address + (data.buildingName ? ` (${data.buildingName})` : '');
      setAddress(fullAddress);
      setPostalCode(data.zonecode);
      setAddressModalOpen(false);
    },
    [setAddressModalOpen],
  );

  // 전화번호 핸들러
  const handlePhoneChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(formatPhoneNumber(e.target.value));
  }, []);

  // 제출 핸들러
  const handleSubmit = useCallback(async () => {
    if (!delivery) return;

    if (!name.trim()) {
      setSubmitError('받는 분 성함을 입력해 주세요.');
      return;
    }

    const cleanPhone = phoneNumber.replace(/-/g, '');
    if (!cleanPhone || !/^01[016789]\d{7,8}$/.test(cleanPhone)) {
      setSubmitError('올바른 휴대폰 번호를 입력해 주세요.');
      return;
    }

    if (!address) {
      setSubmitError('주소를 검색해 주세요.');
      return;
    }

    if (!postalCode) {
      setSubmitError('우편번호가 없습니다. 주소를 다시 검색해 주세요.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const dto: GiftDeliveryAddressDto = {
        name: name.trim(),
        phoneNumber: cleanPhone,
        address,
        addressDetail: addressDetail.trim() || undefined,
        postalCode,
      };

      const updated = await submitGiftDeliveryAddress(delivery._id, dto);
      setDelivery(updated);
    } catch (err) {
      console.error('배송지 입력 실패:', err);
      setSubmitError('배송지 입력에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      setIsSubmitting(false);
    }
  }, [delivery, name, phoneNumber, address, postalCode, addressDetail]);

  const canEditAddress =
    delivery?.deliveryStatus === GiftDeliveryStatus.WaitingAddress ||
    delivery?.deliveryStatus === GiftDeliveryStatus.AddressSubmitted;

  const handleStartEdit = useCallback(() => {
    if (!delivery?.recipient) return;
    setEditName(delivery.recipient.name);
    setEditPhoneNumber(formatPhoneNumber(delivery.recipient.phoneNumber));
    setEditAddress(delivery.recipient.address);
    setEditPostalCode(delivery.recipient.postalCode);
    setEditAddressDetail(delivery.recipient.addressDetail || '');
    setEditError(null);
    setIsEditing(true);
  }, [delivery]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditError(null);
  }, []);

  const handleEditAddressComplete = useCallback(
    (data: Address) => {
      const fullAddress =
        data.address + (data.buildingName ? ` (${data.buildingName})` : '');
      setEditAddress(fullAddress);
      setEditPostalCode(data.zonecode);
      setAddressModalOpen(false);
    },
    [setAddressModalOpen],
  );

  const handleEditSubmit = useCallback(async () => {
    if (!delivery) return;

    if (!editName.trim()) {
      setEditError('받는 분 성함을 입력해 주세요.');
      return;
    }

    const cleanPhone = editPhoneNumber.replace(/-/g, '');
    if (!cleanPhone || !/^01[016789]\d{7,8}$/.test(cleanPhone)) {
      setEditError('올바른 휴대폰 번호를 입력해 주세요.');
      return;
    }

    if (!editAddress) {
      setEditError('주소를 검색해 주세요.');
      return;
    }

    if (!editPostalCode) {
      setEditError('우편번호가 없습니다. 주소를 다시 검색해 주세요.');
      return;
    }

    setIsEditSubmitting(true);
    setEditError(null);

    try {
      const dto: GiftDeliveryAddressDto = {
        name: editName.trim(),
        phoneNumber: cleanPhone,
        address: editAddress,
        addressDetail: editAddressDetail.trim() || undefined,
        postalCode: editPostalCode,
      };

      const updated = await updateGiftDeliveryAddress(delivery._id, dto);
      setDelivery(updated);
      setIsEditing(false);
    } catch (err) {
      console.error('배송지 수정 실패:', err);
      setEditError('배송지 수정에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      setIsEditSubmitting(false);
    }
  }, [
    delivery,
    editName,
    editPhoneNumber,
    editAddress,
    editPostalCode,
    editAddressDetail,
  ]);

  // 로딩
  if (loading) {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  // 에러
  if (error || !delivery) {
    return (
      <div className="center-box min-h-screen flex-col gap-4 px-4 text-center">
        <ExclamationCircleIcon className="h-16 w-16 text-gray-300" />
        <p className="text-gray-500">배송 정보를 찾을 수 없습니다.</p>
        <p className="text-sm text-gray-400">
          유효하지 않은 코드이거나 이미 처리된 배송입니다.
        </p>
      </div>
    );
  }

  // 상태 메시지
  const statusMessage = getDeliveryStatusMessage(delivery.deliveryStatus);

  // 상태별 화면 (배송지 입력 완료 이후)
  if (statusMessage) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-stone-100 to-white px-4 py-8">
        <div className="mx-auto max-w-md">
          <div
            className={`rounded-xl p-6 text-center ${
              statusMessage.type === 'success'
                ? 'border border-stone-200 bg-stone-50'
                : statusMessage.type === 'info'
                ? 'border border-stone-200 bg-stone-50'
                : 'border border-red-100 bg-red-50'
            }`}
          >
            {statusMessage.type === 'success' ? (
              <CheckCircleSolidIcon className="mx-auto h-10 w-10 text-stone-600" />
            ) : statusMessage.type === 'error' ? (
              <ExclamationCircleIcon className="mx-auto h-10 w-10 text-red-400" />
            ) : (
              <LocationMarkerIcon className="mx-auto h-10 w-10 text-stone-500" />
            )}
            <h2 className="mt-3 text-lg font-medium text-stone-800">
              {statusMessage.title}
            </h2>
            <p className="mt-1 whitespace-pre-line text-sm text-stone-500">
              {statusMessage.message}
            </p>
          </div>

          {/* 배송 진행 타임라인 */}
          <div className="mt-4 rounded-xl border border-stone-200 bg-white px-4 py-5">
            <DeliveryTimeline status={delivery.deliveryStatus} />
          </div>

          {/* 상품 정보 */}
          <div className="mt-6 overflow-hidden rounded-xl border border-stone-200 bg-white">
            <div className="aspect-[4/3] w-full overflow-hidden bg-stone-100">
              {imageUrl ? (
                <BucketImg
                  value={imageUrl}
                  size="w400"
                  alt={itemName}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-stone-400">
                  No Image
                </div>
              )}
            </div>
            <div className="p-4">
              <h3 className="text-lg font-medium text-stone-800">{itemName}</h3>
              {delivery.quantity > 1 && (
                <p className="mt-1 text-sm text-stone-500">수량: {delivery.quantity}개</p>
              )}
            </div>
          </div>

          {/* 운송장 정보 */}
          {delivery.shipping?.trackingNumber && (
            <div className="mt-4 rounded-xl border border-stone-200 bg-white p-4">
              <div className="flex items-center gap-2">
                <TruckIcon className="h-4 w-4 text-stone-400" />
                <p className="text-xs font-medium text-stone-400">배송 정보</p>
              </div>
              <div className="mt-3 space-y-2.5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-stone-400">택배사</p>
                    <p className="mt-0.5 text-sm font-medium text-stone-700">
                      {delivery.shipping.carrier || '-'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-stone-400">운송장번호</p>
                    <p className="mt-0.5 text-sm font-medium text-stone-700">
                      {delivery.shipping.trackingNumber}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      if (delivery.shipping?.trackingNumber) {
                        handleCopyTracking(delivery.shipping.trackingNumber);
                      }
                    }}
                    className="flex items-center gap-1 rounded-lg bg-stone-100 px-3 py-1.5 text-xs font-medium text-stone-600 transition active:bg-stone-200"
                  >
                    <ClipboardCopyIcon className="h-3.5 w-3.5" />
                    {copied ? '복사됨' : '복사'}
                  </button>
                </div>
                {delivery.shipping.shippedAt && (
                  <div>
                    <p className="text-xs text-stone-400">발송일</p>
                    <p className="mt-0.5 text-sm text-stone-600">
                      {new Date(delivery.shipping.shippedAt).toLocaleDateString('ko-KR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </p>
                  </div>
                )}
                {delivery.shipping.deliveredAt && (
                  <div>
                    <p className="text-xs text-stone-400">배송완료일</p>
                    <p className="mt-0.5 text-sm text-stone-600">
                      {new Date(delivery.shipping.deliveredAt).toLocaleDateString(
                        'ko-KR',
                        {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        },
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 입력된 배송지 정보 */}
          {delivery.recipient && !isEditing && (
            <div className="mt-4 rounded-xl border border-stone-200 bg-white p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-stone-400">배송지 정보</p>
                {canEditAddress && (
                  <button
                    onClick={handleStartEdit}
                    className="rounded-lg bg-stone-100 px-3 py-1 text-xs font-medium text-stone-600 transition active:bg-stone-200"
                  >
                    수정
                  </button>
                )}
              </div>
              <p className="mt-2 font-medium text-stone-700">{delivery.recipient.name}</p>
              <p className="text-sm text-stone-500">
                {formatPhoneNumber(delivery.recipient.phoneNumber)}
              </p>
              <p className="mt-1 text-sm text-stone-500">
                ({delivery.recipient.postalCode}) {delivery.recipient.address}
              </p>
              {delivery.recipient.addressDetail && (
                <p className="text-sm text-stone-500">
                  {delivery.recipient.addressDetail}
                </p>
              )}
            </div>
          )}

          {/* 배송지 수정 폼 */}
          {isEditing && (
            <div className="mt-4 rounded-xl border border-stone-300 bg-white p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold text-stone-600">배송지 수정</p>
                <button
                  onClick={handleCancelEdit}
                  className="text-xs text-stone-400 transition hover:text-stone-600"
                >
                  취소
                </button>
              </div>

              <div className="mt-3">
                <label className="mb-1 block text-xs font-medium text-stone-500">
                  받는 분 성함
                </label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full rounded-lg border border-stone-200 py-2.5 px-3 text-sm text-stone-700 focus:border-stone-400 focus:outline-none"
                />
              </div>

              <div className="mt-3">
                <label className="mb-1 block text-xs font-medium text-stone-500">
                  연락처
                </label>
                <input
                  type="tel"
                  value={editPhoneNumber}
                  onChange={(e) => setEditPhoneNumber(formatPhoneNumber(e.target.value))}
                  maxLength={13}
                  className="w-full rounded-lg border border-stone-200 py-2.5 px-3 text-sm text-stone-700 focus:border-stone-400 focus:outline-none"
                />
              </div>

              <div className="mt-3">
                <label className="mb-1 block text-xs font-medium text-stone-500">
                  주소
                </label>
                <button
                  onClick={() => setAddressModalOpen(true)}
                  className={`w-full rounded-lg border py-2.5 px-3 text-left text-sm ${
                    editAddress
                      ? 'border-stone-200 text-stone-700'
                      : 'border-dashed border-stone-300 text-stone-400'
                  }`}
                >
                  {editAddress ? (
                    <span>
                      ({editPostalCode}) {editAddress}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <LocationMarkerIcon className="h-4 w-4" />
                      주소 검색
                    </span>
                  )}
                </button>
              </div>

              {editAddress && (
                <div className="mt-2">
                  <label className="mb-1 block text-xs font-medium text-stone-500">
                    상세주소
                  </label>
                  <input
                    type="text"
                    value={editAddressDetail}
                    onChange={(e) => setEditAddressDetail(e.target.value)}
                    placeholder="동/호수 등 상세주소 입력"
                    className="w-full rounded-lg border border-stone-200 py-2.5 px-3 text-sm text-stone-700 focus:border-stone-400 focus:outline-none"
                  />
                </div>
              )}

              {editError && (
                <p className="mt-2 text-center text-xs text-red-500">{editError}</p>
              )}

              <button
                onClick={handleEditSubmit}
                disabled={
                  isEditSubmitting || !editName || !editPhoneNumber || !editAddress
                }
                className={`mt-4 flex w-full items-center justify-center rounded-lg py-3 text-sm font-medium transition ${
                  isEditSubmitting || !editName || !editPhoneNumber || !editAddress
                    ? 'bg-stone-200 text-stone-400'
                    : 'bg-stone-800 text-white active:bg-stone-900'
                }`}
              >
                {isEditSubmitting ? '수정 중...' : '배송지 수정 완료'}
              </button>
            </div>
          )}

          {/* 상품 상세 정보 (접힌 상태) */}
          {detailInfo && (
            <details className="mt-4 rounded-xl border border-stone-200 bg-white">
              <summary className="cursor-pointer px-4 py-3 text-xs font-medium text-stone-400">
                상품 정보 보기
              </summary>
              <div className="border-t border-stone-100 px-4 py-3">
                <p className="whitespace-pre-wrap break-keep text-sm leading-relaxed text-stone-600">
                  {detailInfo}
                </p>
              </div>
            </details>
          )}

          <ContactEmailLink />
        </div>

        {/* 주소 검색 모달 (수정 모드) */}
        <Modal openId={ADDRESS_MODAL_ID} onClose={() => setAddressModalOpen(false)}>
          <DaumPostcode style={{ height: 480 }} onComplete={handleEditAddressComplete} />
        </Modal>
      </div>
    );
  }

  // 배송지 입력 폼
  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-100 to-white pb-8">
      {/* 상품 이미지 */}
      <div className="relative">
        <div className="aspect-[4/3] w-full overflow-hidden bg-stone-200">
          {imageUrl ? (
            <BucketImg
              value={imageUrl}
              size="w400"
              alt={itemName}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-stone-400">
              No Image
            </div>
          )}
        </div>

        {/* 감사 문구 오버레이 */}
        <div className="pointer-events-none absolute left-0 right-0 top-0 bg-gradient-to-b from-black/50 to-transparent px-4 pb-8 pt-3 text-center">
          <p className="text-sm font-bold text-white/90">따뜻한 마음에 감사드립니다.</p>
        </div>
      </div>

      <div className="px-4 pt-5">
        {/* 상품명 */}
        <h2 className="text-base font-medium text-stone-800">{itemName}</h2>
        {delivery.quantity > 1 && (
          <p className="mt-1 text-sm text-stone-500">수량: {delivery.quantity}개</p>
        )}

        {/* 답례글 */}
        {courtesyContent && (
          <div className="mt-4 rounded-xl border border-stone-200 bg-white p-4">
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-stone-600">
              {courtesyContent}
            </p>
          </div>
        )}

        {/* 유효기간 안내 */}
        {delivery.expiresAt && (
          <div className="mt-4 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
            <div>
              <span className="text-sm font-medium text-amber-800">입력 기한</span>
              <p className="text-xs text-amber-600">배송지 입력 가능한 기한</p>
            </div>
            <span className="text-sm font-bold text-amber-800">
              ~
              {new Date(delivery.expiresAt).toLocaleDateString('ko-KR', {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
              })}
            </span>
          </div>
        )}

        {/* 배송지 입력 폼 */}
        <div className="mt-6 rounded-xl border border-stone-200 bg-white p-5">
          <h3 className="text-center text-lg font-bold text-stone-700">
            배송지를 입력해 주세요
          </h3>

          {/* 받는 분 성함 */}
          <div className="mt-5">
            <label className="mb-1.5 block text-sm font-medium text-stone-600">
              받는 분 성함
            </label>
            <div className="relative">
              <UserIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-stone-300" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="홍길동"
                className="w-full rounded-lg border border-stone-200 py-3 pl-10 pr-4 text-stone-700 focus:border-stone-400 focus:outline-none"
              />
            </div>
          </div>

          {/* 연락처 */}
          <div className="mt-4">
            <label className="mb-1.5 block text-sm font-medium text-stone-600">
              연락처
            </label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={handlePhoneChange}
              placeholder="010-1234-5678"
              maxLength={13}
              className="w-full rounded-lg border border-stone-200 py-3 px-4 text-stone-700 focus:border-stone-400 focus:outline-none"
            />
          </div>

          {/* 주소 */}
          <div className="mt-4">
            <label className="mb-1.5 block text-sm font-medium text-stone-600">
              주소
            </label>
            <button
              onClick={() => setAddressModalOpen(true)}
              className={`w-full rounded-lg border py-3 px-4 text-left ${
                address
                  ? 'border-stone-200 text-stone-700'
                  : 'border-dashed border-stone-300 text-stone-400'
              }`}
            >
              {address ? (
                <span>
                  ({postalCode}) {address}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <LocationMarkerIcon className="h-5 w-5" />
                  주소 검색
                </span>
              )}
            </button>
          </div>

          {/* 상세주소 */}
          {address && (
            <div className="mt-3">
              <label className="mb-1.5 block text-sm font-medium text-stone-600">
                상세주소
              </label>
              <input
                type="text"
                value={addressDetail}
                onChange={(e) => setAddressDetail(e.target.value)}
                placeholder="동/호수 등 상세주소 입력"
                className="w-full rounded-lg border border-stone-200 py-3 px-4 text-stone-700 focus:border-stone-400 focus:outline-none"
              />
            </div>
          )}

          {submitError && (
            <p className="mt-3 text-center text-sm text-red-500">{submitError}</p>
          )}

          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !name || !phoneNumber || !address}
            className={`mt-5 flex w-full items-center justify-center rounded-lg py-4 font-medium transition ${
              isSubmitting || !name || !phoneNumber || !address
                ? 'bg-stone-200 text-stone-400'
                : 'bg-stone-800 text-white active:bg-stone-900'
            }`}
          >
            {isSubmitting ? '처리 중...' : '배송지 입력 완료'}
          </button>
        </div>

        {/* 유의사항 */}
        <div className="mt-4 rounded-lg bg-stone-100 p-3">
          <p className="text-xs font-medium text-stone-500">유의사항</p>
          <ul className="mt-1.5 space-y-1 break-keep text-xs text-stone-400">
            <li>• 입력 기한 내에 배송지를 입력해 주세요.</li>
            <li>• 정확한 주소를 입력해 주셔야 정상적으로 배송됩니다.</li>
            <li>• 잘못된 주소 입력으로 인한 배송 오류는 재배송이 어려울 수 있습니다.</li>
          </ul>
        </div>

        {/* 상품 상세 정보 (접힌 상태) */}
        {detailInfo && (
          <details className="mt-4 rounded-xl border border-stone-200 bg-white">
            <summary className="cursor-pointer px-4 py-3 text-xs font-medium text-stone-400">
              상품 정보 보기
            </summary>
            <div className="border-t border-stone-100 px-4 py-3">
              <p className="whitespace-pre-wrap break-keep text-sm leading-relaxed text-stone-600">
                {detailInfo}
              </p>
            </div>
          </details>
        )}

        {/* 이용문의 */}
        <ContactEmailLink className="mt-4" />
      </div>

      {/* 주소 검색 모달 */}
      <Modal openId={ADDRESS_MODAL_ID} onClose={() => setAddressModalOpen(false)}>
        <DaumPostcode
          style={{ height: 480 }}
          onComplete={isEditing ? handleEditAddressComplete : handleAddressComplete}
        />
      </Modal>
    </div>
  );
};

export default GiftDeliveryAddressSubmitPage;
